import { z } from 'zod';
import pRetry from 'p-retry';
import type { Options } from 'p-retry';
import { getTestOverrides } from '@wix/cli-test-overrides';
import type { HttpClient } from '@wix/cli-http-client';
import { CliError, CliErrorCode } from '@wix/cli-error';
import {
  prepareExtensionsForRevision,
  publishUpdateAppEvent,
} from '@wix/ambassador-blocks-v1-blocks-app/http';
import type {
  PrepareExtensionsForRevisionResponse,
  PublishUpdateAppEventRequest,
} from '@wix/ambassador-blocks-v1-blocks-app/types';
import { getApplicationStructure } from '@wix/ambassador-blocks-v1-application-structure/http';
import { ComponentType } from '@wix/dev-center-client';
import { saveAppConfig } from '../files/app.config.js';
import type { ProjectModel } from '../model.js';

export type BlocksComponentsData = Exclude<
  PrepareExtensionsForRevisionResponse['extensionsForRevision'],
  null | undefined
>;

const blocksStructureSchema = z.object({
  applicationStructure: z.object({
    widgets: z.record(
      z.string(),
      z.object({
        elements: z.record(z.string()),
        widgets: z.record(
          z.string(),
          z.object({
            appDefId: z.string(),
            widgetId: z.string(),
          })
        ),
      })
    ),
    panels: z.record(
      z.string(),
      z.object({
        elements: z.record(z.string()),
      })
    ),
  }),
});

export type BlocksStructure = z.infer<typeof blocksStructureSchema>;

const saveBlocksMainComponentId = async (
  projectFolder: string,
  blocksComponentsData: BlocksComponentsData
) => {
  const mainComponent = blocksComponentsData.find(
    (blocksComponent) => blocksComponent.type === ComponentType.STUDIO
  );

  if (mainComponent?.id) {
    await saveAppConfig(projectFolder, {
      mainBlocksComponentId: mainComponent.id,
    });
  }
};

export class BlocksServicesClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    const { minRetryTimeout } = getTestOverrides();

    this.retryOptions = {
      retries: 3,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  private getBlocksComponentsData = async (appId: string, revision: string) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            prepareExtensionsForRevision({
              appDefId: appId,
              documentRevision: revision,
            })
          ),
        this.retryOptions
      );

      return data.extensionsForRevision ?? [];
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetBlocksExtensionsData(),
        cause: e,
      });
    }
  };

  prepareBlocksComponentsData = async ({
    config,
    projectFolder,
  }: ProjectModel) => {
    if (!config.blocks?.uiRevision) {
      return [];
    }
    const blocksComponentsData = await this.getBlocksComponentsData(
      config.appId,
      config.blocks.uiRevision
    );

    await saveBlocksMainComponentId(projectFolder, blocksComponentsData);

    return blocksComponentsData;
  };

  refreshBlocksInEditor = async (payload: PublishUpdateAppEventRequest) => {
    try {
      await pRetry(
        () => this.httpClient.request(publishUpdateAppEvent(payload)),
        this.retryOptions
      );
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUpdateBlocksAfterSync(),
        cause: e,
      });
    }
  };

  getBlocksStructure = async (
    appId: string,
    revision: string
  ): Promise<BlocksStructure> => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            getApplicationStructure({
              appDefId: appId,
              revision: Number(revision),
            })
          ),
        this.retryOptions
      );

      return blocksStructureSchema.parse(data);
    } catch (e) {
      throw new CliError({
        cause: e,
        code: CliErrorCode.FailedToGetBlocksStructure(),
      });
    }
  };
}
